import { createSelector } from 'reselect';
import type { AssetListItem } from '~/data/AssetListItem/AssetListItem';
import type MachineFilterStateRecord from '~/ducks/machine-filter-v2/reducer/MachineFilterStateRecord';
import type { Fields } from '../data/AssetListRecord';

const extractAssets = (assetList: Fields) => assetList.assets;
export const getNumberOfAssets = createSelector([extractAssets], (assets) => {
  return assets.length;
});

const getVisibilityFilter = (machineFilterV2: MachineFilterStateRecord) =>
  machineFilterV2.getShowInMapFilter();

export const getVisibleAssets = createSelector(
  [getVisibilityFilter, (_machineFilter, assets: AssetListItem[]) => assets],
  (visibilityFilter, assets) => {
    if (!visibilityFilter) {
      return assets;
    }

    return assets.filter((asset) => visibilityFilter.assetIds.includes(asset.assetDetails.id));
  }
);

export const keepMapSelectedOnTop = (assets: AssetListItem[], selectedIds: string[]) => {
  const selectedAssets = assets.filter((asset) => selectedIds.includes(asset.assetDetails.id));
  const unselectedAssets = assets.filter((asset) => !selectedIds.includes(asset.assetDetails.id));
  return [...selectedAssets, ...unselectedAssets];
};

export const getSelectableAssets = createSelector(
  [(assets: AssetListItem[]) => assets],
  (assets: AssetListItem[]) => assets.filter((asset) => asset.isSelectable)
);

export const filterSelectedAssetIdsByAssetList = createSelector(
  [(assets: AssetListItem[]) => assets, (_assets, selectedAssetIds: AssetId[]) => selectedAssetIds],
  (assets: AssetListItem[], selectedAssetIds: AssetId[]) => {
    return selectedAssetIds.filter(
      (assetId) => !!assets.find((asset) => asset.assetDetails.id === assetId)
    );
  }
);

export const getSelectedAssets = createSelector(
  [extractAssets, (assetList) => assetList.selectedAssetIds],
  (assets: AssetListItem[], selectedAssetIds: AssetId[]) => {
    return assets.filter((asset) => selectedAssetIds.includes(asset.assetDetails.id));
  }
);

export const getAssets = createSelector(
  [extractAssets, (_assetList, assetIds: AssetId[]) => assetIds],
  (assets: AssetListItem[], assetIds: AssetId[]) => {
    return assets.filter((asset) => assetIds.includes(asset.assetDetails.id));
  }
);

export const findAssetIndex = (assets: AssetListItem[], assetId: AssetId) =>
  assets.findIndex((asset) => asset.assetDetails.id === assetId);
